// @ts-nocheck
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import { headerHideForMobileStyles } from "@store/Themes/common/mixins/header";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Hidden from "@insite/mobius/Hidden";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import { useContext } from "react";
/* Store Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    currentMode: getCookie(NavigationModeCookieName) || "Storefront", // Store Custom Code
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // Starter Custom Code
    wrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    navWrapper?: InjectableCss;
    bottomContentWrapper?: InjectableCss;
    printWrapper?: InjectableCss;
    // Store Custom Code
    mainNavigation?: InjectableCss;
    header?: InjectableCss;
    vmiHeader?: InjectableCss;
    mainNavigationMobile?: InjectableCss;
}

/* Store Custom Code - Start */
const headerZoneStyles: InjectableCss = {
    css: css`
        display: flex;
        align-items: center;
    `,
};
/* Store Custom Code - End */

export const headerStyles: HeaderStyles = {
    skipToContent: {
        skipToContent: {
            css: css`
                &:focus {
                    z-index: 4;
                }
            `,
        },
    },
    /* Store Custom Code - Start */
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding: 20px;
            border-bottom: 1px solid ${getColor("common.border")};

            ${breakpoint("lg")} {
                display: block;
                padding: 0;
                border-bottom: 0;
            }
        `,
    },
    header: headerZoneStyles,
    vmiHeader: {
        css: css`
            ${headerZoneStyles.css}
            background-color: ${getColor("common.accent")};
        `,
    },
    websiteName: {
        css: css`
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    /* Starter Custom Code */
    mainNavigation: {
        css: css`
            ${siteWidthStyles()}
        `,
    },
    mainNavigationMobile: {
        css: css`
            width: 100%;
        `,
    },
    /* Store Custom Code - End */
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    currentMode, // Store Custom Code
}: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();

    /* Store Custom Code - Start */
    const { isEditing } = useContext(ShellContext);

    const printWrapperCss = css`
        ${isEditing ? "display: block;" : "display: none;"}

        img {
            width: auto;
            height: ${pxToRem(50)};
        }

        @media print {
            display: block !important;
        }
    `;
    /* Store Custom Code - End */

    return (
        <>
            {/* Store Custom Code - next line - keep siteName */}
            {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
            <StyledWrapper css={printWrapperCss} /* Store Custom Code */>
                <Zone contentId={id} zoneName="Logo" />
            </StyledWrapper>
            <Page as="header" isHeader={true} {...styles.page}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    {/* Store Custom Code - Start */}
                    <Zone contentId={id} zoneName="SecondaryNavigation" />
                    <StyledWrapper {...styles.container}>
                        <Hidden above="md" {...styles.mainNavigationMobile}>
                            <Zone contentId={id} zoneName="MainNavigation" />
                        </Hidden>
                        <StyledWrapper {...(currentMode === "Vmi" ? styles.vmiHeader : styles.header)}>
                            <Zone contentId={id} zoneName="Header" />
                        </StyledWrapper>
                        <Hidden below="lg">
                            <Zone contentId={id} zoneName="MainNavigation" />
                        </Hidden>
                    </StyledWrapper>
                    {/* Store Custom Code - End */}
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
